import { TOKEN_IDS, type TokenId } from "@/types";
import { type Address, type WalletClient, encodeFunctionData } from "viem";
import { readContractMethod } from "@/utils";
import { ORACLE_CONFIG, TOKEN_ADDRESSES, TOKEN_CONFIG } from "@/config";

// ABI for the price function
const PRICE_ORACLE_ABI = [
    {
        inputs: [
            {
                internalType: "address",
                name: "_asset",
                type: "address",
            },
        ],
        name: "price",
        outputs: [
            {
                internalType: "uint256",
                name: "",
                type: "uint256",
            },
        ],
        stateMutability: "view",
        type: "function",
    },
];

// Helper function to prepare price call
export const preparePriceCall = (assetAddress: Address, chainId: number) => ({
    to: ORACLE_CONFIG[chainId as keyof typeof ORACLE_CONFIG] as Address,
    data: encodeFunctionData({
        abi: PRICE_ORACLE_ABI,
        functionName: "price",
        args: [assetAddress],
    }),
});

export const fetchPrice = async (unit: TokenId, walletClient?: WalletClient): Promise<number> => {
    if (!walletClient) {
        throw new Error("Wallet client is required");
    }

    if (!walletClient.chain?.id) {
        throw new Error("Chain ID is not available");
    }

    if (unit === TOKEN_IDS.USDT) {
        unit = TOKEN_IDS.USDC;
    }

    try {
        const tokenAddress = TOKEN_ADDRESSES[walletClient.chain.id][unit];

        if (!tokenAddress) {
            throw new Error(`Token address not found for ${unit}`);
        }

        // Prepare the contract call
        const priceCall = preparePriceCall(tokenAddress, walletClient.chain.id);

        // Call the contract method
        const priceResult = await readContractMethod(walletClient, priceCall);

        const decimals = TOKEN_CONFIG[unit].decimals;
        const decimalOffset = 18 + 18 - decimals;

        // Convert from wei/big number to regular number
        // Note: You may need to adjust decimals based on your oracle's price format

        const price = Number(BigInt(priceResult)) / 10 ** decimalOffset; // Assuming 18 decimals for price

        return price;
    } catch (error) {
        console.error(`Error fetching price for token ${unit}:`, error);
        throw new Error(`Failed to fetch price for token ${unit}`);
    }
};
